import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Row, Col, Typography, Button } from 'antd';
import { useParams } from 'react-router-dom';
import { fetchInquiryById } from '../../slices/inquiry';
import {fetchSalesOrderById} from '../../slices/sales-order';
import { useDispatch } from 'react-redux';
import { ReactComponent as PrintIcon } from '../../Images/PrintIcon.svg';
import dayjs from 'dayjs';
import logo from '../../Images/logo.svg';

const PrintSalesOrder = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const [salesOrderData, setSalesOrderData] = useState(null);
    const [inquiryData, setInquiryData] = useState(null);
    const contentRef = useRef();

    const fetchData = useCallback(async () => {
        try {
            // Step 1: Fetch quotation by ID
            const salesOrderResponse = await dispatch(fetchSalesOrderById(id)).unwrap();
           

            const salesOrder = salesOrderResponse.data;
            setSalesOrderData(salesOrder);

            // Step 2: Fetch inquiry by inquiry_id from quotation
            if (salesOrder?.inquiry) {
                const inquiryResponse = await dispatch(fetchInquiryById(salesOrder.inquiry)).unwrap();
               

                setInquiryData(inquiryResponse.data);
            } else {
                console.warn('No inquiry_id found in quotation data');
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }, [dispatch, id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);



    // --- Build children for Engineering group (show header only if any record needs it) ---
    const engineeringChildren = [];
    const engineeringHasCutting = salesOrderData?.inquiry_salesorder_details?.some(r => r.cutting);
    const engineeringHasMachining = salesOrderData?.inquiry_salesorder_details?.some(r => r.machining);
    const engineeringHasDeburring = salesOrderData?.inquiry_salesorder_details?.some(r => r.deburring);

    if (engineeringHasCutting) {
        engineeringChildren.push({
            title: (
                <>
                    Cutting<br />Price
                </>
            ),
            dataIndex: 'cutting_price',
            key: 'cutting_price',
            align: 'center',
            width: 120,
            render: (_, record) => record?.cutting ? (record.cutting_price ? `Rs. ${record.cutting_price} /-` : 'N/A') : '',
        });
    }
    if (engineeringHasMachining) {
        engineeringChildren.push({
            title: (
                <>
                    Machining<br />Price
                </>
            ),
            dataIndex: 'machining_price',
            key: 'machining_price',
            align: 'center',
            width: 120,
            render: (_, record) => record?.machining ? (record.machining_price ? `Rs. ${record.machining_price} /-` : 'N/A') : '',
        });
    }
    if (engineeringHasDeburring) {
        engineeringChildren.push({
            title: (
                <>
                    Deburring<br />Price
                </>
            ),
            dataIndex: 'deburring_price',
            key: 'deburring_price',
            align: 'center',
            width: 120,
            render: (_, record) => record?.deburring ? (record.deburring_price ? `Rs. ${record.deburring_price} /-` : 'N/A') : '',
        });
    }

    // --- Build children for Surface Treatment group (show header only if any record needs it) ---
    const surfaceTreatmentChildren = [];
    const surfaceHasAnodising = salesOrderData?.inquiry_salesorder_details?.some(r => r.anodising);
    const surfaceHasPowder = salesOrderData?.inquiry_salesorder_details?.some(r => r.powder_coating);
    const surfaceHasPVDF = salesOrderData?.inquiry_salesorder_details?.some(r => r.pvdf);

    if (surfaceHasAnodising) {
        surfaceTreatmentChildren.push({
            title: (
                <>
                    Anodising<br />Price
                </>
            ),
            dataIndex: 'anodising_price',
            key: 'anodising_price',
            align: 'center',
            width: 120,
            render: (_, record) => record?.anodising ? (record.anodising_price ? `Rs. ${record.anodising_price} /-` : 'N/A') : '',
        });
    }
    if (surfaceHasPowder) {
        surfaceTreatmentChildren.push({
            title: (
                <>
                    Powder<br />Coating<br />Price
                </>
            ),
            dataIndex: 'powder_coating_price',
            key: 'powder_coating_price',
            align: 'center',
            width: 120,
            render: (_, record) => record?.powder_coating ? (record.powder_coating_price ? `Rs. ${record.powder_coating_price} /-` : 'N/A') : '',
        });
    }
    if (surfaceHasPVDF) {
        surfaceTreatmentChildren.push({
            title: (
                <>
                    PVDF<br />Price
                </>
            ),
            dataIndex: 'pvdf_price',
            key: 'pvdf_price',
            align: 'center',
            width: 120,
            render: (_, record) => record?.pvdf ? (record.pvdf_price ? `Rs. ${record.pvdf_price} /-` : 'N/A') : '',
        });
    }

    // --- Combine groups under one Surface Finish column ---
    // also include Mill Finish as a simple sibling if you want (uses surface_finish_detail to detect)
    const surfaceFinishColumn = {
        title: 'Surface Finish',
        align: 'center',
        children: [
            // Mill Finish (leaf)
            {
                title: 'Mill Finish',
                key: 'mill_finish',
                align: 'center',
                width: 90,
                render: (_, record) =>
                    Array.isArray(record.surface_finish_detail) && record.surface_finish_detail.some(f => f.name === 'Mill Finish')
                        ? 'Yes'
                        : '',
            },

            // Engineering group (child with children-of-child)
            ...(engineeringChildren.length
                ? [
                    {
                        title: 'Engineering',
                        align: 'center',
                        children: engineeringChildren,
                    },
                ]
                : []),

            // Surface treatment group (child with children-of-child)
            ...(surfaceTreatmentChildren.length
                ? [
                    {
                        title: 'Surface treatment',
                        align: 'center',
                        children: surfaceTreatmentChildren,
                    },
                ]
                : []),

        ].filter(Boolean),
    };


    const columns = [
        {
            title: 'Sr No.',
            dataIndex: 'sr',
            width: 40,
            align: 'center',
            className: 'col-sr',
            render: (_text, _record, index) => index + 1,
        },
        {
            title: 'Section No',
            dataIndex: 'section_no',
            width: 60,
            align: 'center',
            className: 'col-profile',
            render: (_text, _record) => (
                <span>{_record?.section_no}</span>
            ),
        },
        {
            title: 'Length (MM)',
            dataIndex: 'length',
            width: 60,
            align: 'center',
            render: (_text, _record) => (
                <span>{_record?.length}</span>
            ),
        },
        {
            title: 'ALLOY/TEMP',
            dataIndex: 'grade',
            width: 95,
            align: 'center',
            className: 'col-alloy',
            render: (_text, _record) => (
                <div>
                    {_record.alloy_detail?.name || ''} / {_record.temper_detail?.name || ''} ({_record.temper_detail?.code || ''})
                </div>
            ),
        },
        {
            title: 'Rate Per Kg.',
            width: 150,
            render: (_text, _record) => (
                <span>Rs.{_record?.price_per_kg || 'N/A'} /-</span>
            ),
        },
        {
            title: 'Quantity (KG)',
            dataIndex: 'quantity',
            width: 60,
            className: 'col-profile',
            render: (_text, _record) => (
                <span>{_record?.quantity || 'N/A'}</span>
            ),
        },
        {
            title: 'Pieces',
            dataIndex: 'pieces',
            width: 60,
            align: 'center',
            render: (_text, _record) => (
                <span>{_record?.pieces || 'N/A'}</span>
            ),
        },
        surfaceFinishColumn,
        {
            title: 'Conversion',
            dataIndex: 'conversion',
            width: 100,
            align: 'center',
            render: (_text, _record) => (
                <span>{_record?.conversion ? `Rs. ${_record.conversion} /-` : 'N/A'}</span>
            ),
        },
        {
            title: 'Packing Cost',
            dataIndex: 'packing_cost',
            width: 100,
            align: 'center',
            render: (_text, _record) => (
                <span>{_record?.packing_cost ? `Rs. ${_record.packing_cost} /-` : 'N/A'}</span>
            ),
        },
        {
            title: 'Basic Amount',
            dataIndex: 'total_basic_amount',
            width: 90,
            align: 'right',
             render: (_text, record) => {
                return <span>{record?.total_basic_amount}</span>;
            },
        },
        {
            title: 'GST',
            textAlign: 'center',
            width: 80,
            align: 'right',
             render: (_text, record) => {
                return <span>{record?.gst_rate}</span>;
            },
        },
        {
            title: 'Total',
            dataIndex: 'total_amount',
            width: 80,
            align: 'right',
            render: (_text, record) => {
                return <span>{record?.total_amount}</span>;
            },
        },
    ];

    const getMaxColumnDepth = columns => {
        let maxDepth = 1;
        columns.forEach(col => {
            if (col.children) {
                const childDepth = getMaxColumnDepth(col.children) + 1;
                maxDepth = Math.max(maxDepth, childDepth);
            }
        });
        return maxDepth;
    };

    const renderHeaderRows = () => {
        const maxDepth = getMaxColumnDepth(columns);
        const headerRows = [];

        const collectHeadersAtDepth = (cols, depth, currentDepth = 0) => {
            const headers = [];

            cols.forEach((col, index) => {
                const colSpan = getColSpan(col);

                if (currentDepth === depth) {
                    headers.push(
                        <th
                            key={col.key || index}
                            colSpan={colSpan}
                            rowSpan={col.children ? 1 : maxDepth - currentDepth}
                            style={{
                                border: '1px solid #dee2e6',
                                padding: '12px 8px',
                                backgroundColor: '#f8f9fa',
                                textAlign: 'center',
                                fontWeight: '600',
                                fontSize: '14px'
                            }}
                        >
                            {typeof col.title === 'function' ? col.title() : col.title}
                        </th>,
                    );
                } else if (col.children) {
                    headers.push(
                        ...collectHeadersAtDepth(col.children, depth, currentDepth + 1),
                    );
                }
            });

            return headers;
        };

        for (let depth = 0; depth < maxDepth; depth++) {
            headerRows.push(
                <tr key={`header-row-${depth}`} style={{ backgroundColor: '#f8f9fa' }}>
                    {collectHeadersAtDepth(columns, depth)}
                </tr>,
            );
        }

        return headerRows;
    };

    const getColSpan = column => {
        if (column.children) {
            return column.children.reduce((sum, child) => sum + getColSpan(child), 0);
        }
        return 1;
    };

    const renderCellContent = (column, record, index) => {
        if (column.render) {
            const result = column.render(record[column.dataIndex], record, index);
            return React.isValidElement(result) ? result : String(result || '');
        }
        return record[column.dataIndex] || '';
    };

    const getLeafColumns = () => {
        const leafColumns = [];

        columns.forEach(column => {
            if (column.children) {
                column.children.forEach(child => {
                    if (child.children) {
                        leafColumns.push(...child.children);
                    } else {
                        leafColumns.push(child);
                    }
                });
            } else {
                leafColumns.push(column);
            }
        });

        return leafColumns;
    };

    const leafColumns = getLeafColumns();

    const printStyles = `
        @media print {
            * {
                margin: 0 !important;
                padding: 0 !important;
                box-sizing: border-box !important;
            }
            
            body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
                font-size: 12px !important;
                line-height: 1.4 !important;
                color: #000 !important;
                background: white !important;
            }
            
            .print-container-quotation {
                padding: 10px !important;
                width: 100% !important;
                max-width: none !important;
                margin: 0 !important;
                background: white !important;
                box-shadow: none !important;
            }
            
            .terms-and-bank-container {
                padding: 10px !important;
                width: 100% !important;
                
            }
            
            @page {
                size: A4 !important;
                margin: 10mm !important;
            }
            
            .main-heading {
                font-size: 18px !important;
                font-weight: 600 !important;
                text-align: center !important;
                margin: 20px 0 !important;
                color: #000 !important;
            }
            
            .header-text {
                font-size: 14px !important;
                line-height: 1.5 !important;
            }
            
            .customer-details-header {
                font-weight: 600 !important;
                font-size: 16px !important;
                margin-bottom: 8px !important;
                color: #333 !important;
            }
            
            table {
                width: 100% !important;
                border-collapse: collapse !important;
                margin: 20px 0 !important;
                border: 1px solid #c0bfbfff !important;
            }
            
            table th,
            table td {
                border: 1px solid #c0bfbfff !important;
                padding: 8px 6px !important;
                text-align: center !important;
                vertical-align: middle !important;
                font-size: 11px !important;
                word-wrap: break-word !important;
            }
            
            table thead th {
                background-color: #ffffffff !important;
                font-weight: 600 !important;
                font-size: 12px !important;
                border-bottom: 2px solid #c0bfbfff !important;
            }
            
            table thead {
                display: table-header-group !important;
            }
            
            .summary-row td {
                background-color: #ffffffff !important;
                font-weight: 600 !important;
                font-size: 12px !important;
            }
            
            .signature-section {
                margin-top: 60px !important;
                display: flex !important;
                justify-content: space-between !important;
            }
            
            .signature-section h5 {
                font-size: 14px !important;
                color: #12406a !important;
                margin-top: 40px !important;
            }
            
            .terms-heading {
                font-size: 18px !important;
                font-weight: 700 !important;
                text-align: center !important;
                color: #000 !important;
                margin-bottom: 15px !important;
            }
            
            .terms__content {
                font-size: 9px !important;
                line-height: 1.2 !important;
                text-align: justify !important;
            }
            
            .bankDetails {
                background: #ffffffff !important;
                padding: 15px !important;
                border-left: 4px solid #ffffffff !important;
                margin-top: 20px !important;
            }
            
            .bankDetails b {
                font-weight: 600 !important;
            }
            
            .no-print {
                display: none !important;
            }
            
            .logo-section img {
                max-width: 180px !important;
                height: auto !important;
            }
            
            .ant-row {
                display: flex !important;
                width: 100% !important;
            }
            
            .ant-col {
                flex: 1 !important;
            }
        }
    `;

    const commonStyles = `
        * {
          font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji !important;
        }
        body {
          font-size: 16px !important;
          font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji !important;
        }
        .main-container {
          width: 100%;
          padding: 0;
        }
        .ant-col {
          font-size: 16px !important;
        }
        .h1, .h2, .h3, .h4, .h5, .h6, h1, h2, h3, h4, h5, h6 {
          margin-bottom: .5rem;
          font-family: inherit;
          font-weight: 500;
          font-size: 16px;
          line-height: 1.2;
          color: inherit;
        }
        table {
          color: black;
 
          text-align: center;
          border-collapse: collapse;
        }
        table, th, td {
          text-align: center;
          border-collapse: collapse;
        }
        .table-bordered {
          border: 1px solid #dee2e6;
        }
        .table-bordered td, .table-bordered th {
          border: 1px solid #dee2e6;
        }
        .table-bordered td, .table-bordered thead th {
          border-bottom-width: 2px;
        }
        .table thead th {
          vertical-align: bottom;
          border-bottom: 2px solid #dee2e6;
        }
        .table-bordered th {
          border: 1px solid #dee2e6;
        }
        .table td, .table th {
          padding: .75rem;
          vertical-align: top;
          border-top: 1px solid #dee2e6;
        }
        .header-section {
          text-align: center;
          margin-bottom: 20px;
        }
        .main-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
        }
        .sub-header {
          font-size: 24px;
          font-weight: bold;
          margin: 0;
        }
        .copy-label {
          float: right;
          font-size: 18px;
          font-weight: normal;
        }
        .info-section {
          display: flex;
          justify-content: space-between;
          margin: 20px 0;
          font-size: 16px;
        }
        .info-section div {
          font-size: 16px;
        }
        .signature-section {
          margin-top: 100px;
          font-size: 16px;
        }
  `;

    const memoPrintStyles = useMemo(() => printStyles, [printStyles]);
    const memoCommonStyles = useMemo(() => commonStyles, [commonStyles]);

    useEffect(() => {
        const styleEl = document.createElement('style');
        styleEl.innerHTML = memoPrintStyles + memoCommonStyles;
        document.head.appendChild(styleEl);
        return () => document.head.removeChild(styleEl);
    }, [memoPrintStyles, memoCommonStyles]);

    const handlePrint = () => {
        window.print();
    };

    return (
        <>
            <div
                className="print-container-quotation"
                style={{ padding: '10px', background: 'white' }}
                ref={contentRef}
            >
                <div className="page-content">
                    <Row
                        justify="space-between"
                        align="middle"
                        style={{ marginBottom: '40px', marginTop: '5px' }}
                    >
                        <Col
                            span={24}
                            className="logo-section"
                            style={{ textAlign: 'right' }}
                        >
                            <img src={logo} alt="Company Logo" style={{ maxWidth: '180px' }} />
                        </Col>
                    </Row>
                    <Row
                        justify="space-between"
                        align="middle"
                        style={{ marginBottom: '40px' }}
                    >
                        <Col span={24}>
                            <div style={{ textAlign: 'center' }}>
                                <h5 className="main-heading">
                                    Sales Order
                                </h5>
                            </div>
                        </Col>
                    </Row>

                    <Row
                        justify="space-between"
                        align="top"
                        style={{ marginBottom: '40px', width: '100%' }}
                    >
                        <Col span={12}>
                            <div className="customer-details">
                                <h6 className="customer-details-header">Party Name: {salesOrderData?.customer_name}</h6>
                                <span className="header-text">
                                    <strong>Address: </strong> {inquiryData?.contact_persons[0]?.address}<br />
                                    <strong>Email: </strong> {inquiryData?.contact_persons[0]?.email}<br />
                                    <strong>Mob: </strong> {inquiryData?.contact_persons[0]?.mobile}<br />
                                </span>
                            </div>
                        </Col>
                        <Col span={12} style={{ textAlign: 'right' }} className="date-quotation">
                           
                            <div>
                                <span className="header-text">
                                    <strong>Order No: </strong>
                                    {salesOrderData?.sales_order_no}
                                </span>
                            </div>
                             <div>
                                <span className="header-text">
                                    <strong>Order Date: </strong>
                                    {salesOrderData?.order_date
                                        ? dayjs(salesOrderData.order_date).format('DD-MM-YYYY')
                                        : 'N/A'}
                                </span>
                            </div>
                            <div>
                                <span className="header-text">
                                    <strong>PARTY P.0. NO : </strong>
                                    {salesOrderData?.purchase_order_number}
                                </span>
                            </div>
                            <div>
                                <span className="header-text">
                                    <strong>P.0. Date: </strong>
                                    {salesOrderData?.purchase_order_date
                                        ? dayjs(salesOrderData.purchase_order_date).format('DD-MM-YYYY')
                                        : 'N/A'}
                                </span>
                            </div>
                        </Col>
                    </Row>

                    <div style={{ width: '100%', marginBottom: '10px' }}>
                        <table
                            style={{
                                width: '100%',
                                border: '1px solid #dee2e6',
                                marginTop: '30px',
                                borderCollapse: 'collapse'
                            }}
                        >
                            <thead>{renderHeaderRows()}</thead>
                            <tbody>
                                {salesOrderData?.inquiry_salesorder_details?.map((record, rowIndex) => (
                                    <tr key={record.id || rowIndex}>
                                        {leafColumns.map((column, colIndex) => (
                                            <td
                                                key={column.key || colIndex}
                                                className={column.className}
                                                style={{
                                                    border: '1px solid #dee2e6',
                                                    padding: '12px 8px',
                                                    textAlign: column.align || 'center',
                                                    fontSize: '14px'
                                                }}
                                            >
                                                {renderCellContent(column, record, rowIndex)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}

                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div
                className="terms-and-bank-container"
                style={{
                    padding: '20px',
                    background: 'white'
                }}
            >

                <div className="bank-details-wrapper">
                    <Row
                        justify="space-between"
                        align="middle"
                        style={{ marginBottom: '40px', marginTop: '5px' }}
                    >
                        <Col
                            span={24}
                            className="logo-section"
                            style={{ textAlign: 'right' }}
                        >
                            <h3>For ALUKA EXTRUSION INDUSTRIES</h3>
                        </Col>
                    </Row>
                </div>
            </div>

            <div
                className="no-print"
                style={{
                    textAlign: 'center',
                    marginTop: '20px',
                    padding: '20px',
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    zIndex: 1000
                }}
            >
                <Button
                    type="primary"
                    size="large"
                    onClick={handlePrint}
                    icon={<PrintIcon />}
                    style={{
                        boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)',
                        borderRadius: '8px'
                    }}
                >
                    Print Sales Order
                </Button>
            </div>
        </>
    );
};

export default PrintSalesOrder;
